import React, { forwardRef, useEffect, useRef, useState } from "react";
import * as PropTypes from "prop-types";
import Grid from "@mui/material/Grid2";
import PencilIcon from "../../../assets/images/custom/pencil-alt.svg";
import AnalogIcon from "../../../assets/images/custom/analog_icon.svg";
import DigitalIcon from "../../../assets/images/custom/digital_icon.svg";
import { Box, Button, CircularProgress, IconButton, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import localizationKeys from "../../i18n/localizationKeys";
import { useTranslation } from "react-i18next";
import { isAnalogChannel, isDigitalChannel } from "../../constants/channel/config";
import DeleteIcon from "../trendPage/icon/deleteIcon";
import { createChannelGroup, deleteChannelGroup, fetchChannelConfig, fetchChannelModAllData, updateChannelGroup } from "../../api/channel";
import { getComparator, stableSort } from "./utils/helper";
import { RemoveIcon } from "../trendPage/customGraph/removeIcon";
import { GROUP_PROMPTS } from "../../constants/channel/customGroup";
import { TableVirtuoso } from 'react-virtuoso';
import { VSTableBodyCellStyled, VSTableCellStyled, VSTableRowStyled } from "./utils/vsTableStyled";
import { ResetIcon } from "../alarmPage/resetIcon";
import { customChannelSelectorStyles } from "../trendPage/customGraph/virtualScrollTableStyle";

const styles = {
  ...customChannelSelectorStyles,
  chartTitle: {
    fontSize: "18px",
    width: "100%",
    position: "relative",
    "& input": {
      padding: "5px 41px 9px",
      minWidth: "50px",
      width: "calc(100%)",
      color: "#ffffff",
      background: "rgba(1, 1, 1, 0)",
      border: 0,
      "&:hover": {
        padding: "4px 40px 8px",
        borderRadius: "4px",
        border: "solid 1px rgba(156, 156, 156, 0.5)",
      },
      "&:focus": {
        padding: "4px 40px 8px",
        borderRadius: "4px",
        border: "solid 1px #9c9c9c",
        backgroundColor: "rgba(44, 58, 72, 0.3)",
      },
      "&:focus-visible": {
        outline: 'none'
      }
    },
    "&:hover": {
      "& input": {
        padding: "4px 40px 8px",
        borderRadius: "4px",
        border: "solid 1px rgba(156, 156, 156, 0.5)",
      },
    },
  },
};

export const ChannelGroupRegisterPage = ({
  vesselId,
  channelGroups,
  setChannelGroups,
  isCreatingNewGroup,
  setIsCreatingNewGroup,
  selectedChannelGroup,
  setSelectedChannelGroup,
  previousSelectedChannelGroup,
  isEditingGroup,
  setIsEditingGroup,
  setShowPrompt,
  setPromptContent,
  selectedChannels,
  setSelectedChannels,
  setIsUpdateFetch,
}) => {
  const { t } = useTranslation();
  const [channels, setChannels] = useState([]);
  const customGroupTitle = isEditingGroup ? selectedChannelGroup.name : `New Custom Group-${channelGroups.length - 1}`;
  const [searchValue, setSearchValue] = useState("");
  const [filteredChannels, setFilteredChannels] = useState([]);
  const [fetching, setFetching] = useState(false);
  const groupTitleRef = useRef(null);


  const fetchData = async () => {
    setFetching(true);
    const names = (await fetchChannelModAllData(vesselId))?.channelData;
    const config = (await fetchChannelConfig(vesselId))?.config;
    const data = names.map(ch => {
      const chConfig = config.find(chCon => chCon.chNo === ch.chNo);
      return {
        ...ch,
        chType: chConfig?.chType,
        sensorType: chConfig?.sensorType,
        status: chConfig?.status,
        selected: selectedChannels?.some((chNo) => Number(chNo) === Number(ch.chNo)),
      }
    });
    const sortedChannels = stableSort(data, getComparator("desc", "chNo"));
    setChannels(sortedChannels ?? []);
    setFetching(false);
  };

  const updateSearchValue = (value) => {
    setSearchValue(value);
    value = value?.toLowerCase();
    if (value) {
      setFilteredChannels(
        channels.filter(
          (ch) => ch.chName?.toLowerCase().includes(value) || ch.chNo.toString().includes(value)
        )
      );
    } else {
      setFilteredChannels(channels);
    }
  };

  const updateSelectedChannels = (chNo, channel, selected) => {
    if (selected) {
      setSelectedChannels([...selectedChannels, chNo]);
      channel.selected = true;
    } else {
      setSelectedChannels([...selectedChannels].filter((ch) => ch !== chNo));
      channel.selected = false;
    }
  }

  const showAll = () => {
    updateSearchValue("")
  };

  const onSave = () => {
    const okHandler = async () => {
      if (isCreatingNewGroup) {
        const newGroup = channelGroups.at(-1);
        newGroup.name = groupTitleRef.current.value;
        newGroup.values = selectedChannels;
        previousSelectedChannelGroup.current = null;
        setSelectedChannels([]);
        await createChannelGroup(vesselId, newGroup);
        setIsUpdateFetch(true);
      } else if (isEditingGroup) {
        const updatedGroup = channelGroups.find((group) => group.id === selectedChannelGroup.id);
        updatedGroup.name = groupTitleRef.current.value;
        updatedGroup.values = selectedChannels;
        setSelectedChannelGroup(updatedGroup);
        setIsEditingGroup(false);
        previousSelectedChannelGroup.current = updatedGroup;
        setSelectedChannels([]);
        await updateChannelGroup(vesselId, updatedGroup.id, updatedGroup);
        setIsUpdateFetch(true);
      }
      setShowPrompt(false)
    }
    const cancelHandler = () => {
      setShowPrompt(false);
    }
    setPromptContent({ ...GROUP_PROMPTS.SAVE, okHandler, cancelHandler })
    setShowPrompt(true);
  }

  const onCancel = () => {
    const okHandler = () => {
      if (isCreatingNewGroup) {
        setIsCreatingNewGroup(false);
        setChannelGroups(channelGroups.slice(0, -1));
      } else if (isEditingGroup) {
        setIsEditingGroup(false);
      }
      setSelectedChannels([]);
      setSelectedChannelGroup(previousSelectedChannelGroup.current);
      setShowPrompt(false);
    }
    const cancelHandler = () => {
      setShowPrompt(false);
    }
    setPromptContent({ ...GROUP_PROMPTS.CANCEL, okHandler, cancelHandler })
    setShowPrompt(true);
  }

  const onDelete = () => {
    const okHandler = async () => {
      await deleteChannelGroup(vesselId, selectedChannelGroup.id);
      setSelectedChannelGroup(channelGroups[0]);
      setSelectedChannels([]);
      setIsEditingGroup(false);
      setShowPrompt(false);
      setIsUpdateFetch(true);
    }
    const cancelHandler = () => {
      setShowPrompt(false);
    }
    setPromptContent({ ...GROUP_PROMPTS.DELETE, okHandler, cancelHandler, isDelete: true });
    setShowPrompt(true);
  }

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!channelGroups.find((group) => group.id === selectedChannelGroup.id)) {
      setSelectedChannelGroup(channelGroups[0]);
      setIsCreatingNewGroup(false);
      setIsEditingGroup(false);
      previousSelectedChannelGroup.current = null;
    }
  }, [vesselId])

  useEffect(() => {
    if (searchValue === "") {
      updateSearchValue("");
    }
  }, [channels])

  const TableHeader = () => (
    <VSTableRowStyled sx={{ ...styles.headerHolder }}>
      <VSTableCellStyled key={`custom-header-1`} width={"15%"} sx={{ borderBottom: "none" }}>
        {t(localizationKeys.CustomChannelNo)}
      </VSTableCellStyled>
      <VSTableCellStyled key={`custom-header-2`} width={"40%"} sx={{ borderBottom: "none" }}>
        {t(localizationKeys.CustomChannelName)}
      </VSTableCellStyled>
      <VSTableCellStyled key={`custom-header-3`} width={"15%"} sx={{ borderBottom: "none" }}>
        {t(localizationKeys.CustomUnit)}
      </VSTableCellStyled>
      <VSTableCellStyled key={`custom-header-4`} width={"20%"} sx={{ borderBottom: "none" }}>
        {t(localizationKeys.CustomSignalType)}
      </VSTableCellStyled>
      <VSTableCellStyled key={`custom-header-5`} width={"10%"} sx={{ borderBottom: "none" }} />
    </VSTableRowStyled>
  );

  const TableCustBody = (channel) => {
    return (
      <>
        <VSTableBodyCellStyled component="th" scope="row" selected={channel.selected}>
          {channel.chNo}
        </VSTableBodyCellStyled>
        <VSTableBodyCellStyled align="left" selected={channel.selected}>{channel.chName}</VSTableBodyCellStyled>
        <VSTableBodyCellStyled align="left" selected={channel.selected}>{channel.unit}</VSTableBodyCellStyled>
        <VSTableBodyCellStyled align="left" selected={channel.selected}>
          {isAnalogChannel(channel?.chType)
            ? <Box sx={{ borderRadius: "25px", border: "1px solid rgba(250, 48, 248, 0.7)", padding: "1.5px 0 0.5px 6px", width: "72px" }}>
              <img src={AnalogIcon} style={{ marginBottom: "-4px" }} />
              <span style={{ marginLeft: "1px" }}>Analog</span>
            </Box>
            : isDigitalChannel(channel?.chType)
              ? <Box sx={{ borderRadius: "25px", border: "1px solid rgba(132, 235, 128, 0.7)", padding: "1.5px 0 0.5px 6px", width: "72px" }}>
                <img src={DigitalIcon} style={{ marginBottom: "-4px" }} />
                <span style={{ marginLeft: "1px" }}>Digital</span>
              </Box>
              : <Box sx={{ borderRadius: "25px", border: "1px solid rgba(132, 235, 128, 0.7)", padding: "1.5px 0 0.5px 6px", width: "72px" }}>
                <img src={DigitalIcon} style={{ marginBottom: "-4px" }} />
                <span style={{ marginLeft: "1px" }}>Other</span>
              </Box>
          }
        </VSTableBodyCellStyled>
        <VSTableBodyCellStyled align="right" selected={channel.selected}>
          <input
            type="checkbox"
            key={channel.chNo}
            id={channel.chNo}
            value={channel.chNo}
            checked={channel.selected}
            data-ch-type={channel.chType}
            onChange={(event) => {
              updateSelectedChannels(channel.chNo, channel, event.target.checked);
            }}
          />
        </VSTableBodyCellStyled>
      </>
    )
  };
  const VirtuosoTableComponents = {
    Table: (props) => (<Table stickyHeader {...props} />),
    TableHead: forwardRef((props, ref) => (<TableHead {...props} ref={ref} />)),
    TableRow,
    TableBody: forwardRef((props, ref) => (<TableBody {...props} ref={ref} />)),
  }

  const displaySelectedChannel = (chNo) => {
    const channel = channels.find((c) => Number(c.chNo) === Number(chNo));
    return (
      <Box key={chNo} sx={{ ...styles.line, border: isAnalogChannel(channel?.chType) ? "1px solid #fa30f8b3" : "1px solid #84eb80b3" }}>
        {isAnalogChannel(channel?.chType)
          ? <img src={AnalogIcon} style={{ marginBottom: "-1px", marginRight: "8px" }} />
          : <img src={DigitalIcon} style={{ marginBottom: "-1px", marginRight: "8px" }} />
        }
        <Box sx={styles.lineTitle}>{channel.chName}</Box>
        <Box sx={styles.lineIcon} onClick={() => updateSelectedChannels(chNo, channel, false)}>
          <RemoveIcon />
        </Box>
      </Box>
    );
  };

  return (
    <Grid sx={{ color: "white", paddingTop: "20px", paddingLeft: "12px" }}>
      <Grid sx={styles.titleContainer}>
        <Box sx={styles.chartTitle} >
          <Box sx={styles.titleIcon}>
            <img src={PencilIcon} width={19} height={20} style={{ cursor: 'pointer' }} />
          </Box>
          <input
            defaultValue={customGroupTitle}
            size={60}
            maxLength={31}
            ref={groupTitleRef}
          />
        </Box>
      </Grid>
      <Grid container sx={styles.body} spacing={2}>
        <Grid item size={{ xs: 6 }}>
          <Box sx={styles.searchControl}>
            <Box>{t(localizationKeys.CustomSearchChannels)}</Box>
            <Box>
              <Box
                component="input"
                onChange={(evt) => updateSearchValue(evt.target.value)}
                value={searchValue}
                placeholder={t(localizationKeys.CustomSearchChannelsPlaceholder)}
                size="50"
                sx={{ ...styles.searchInput, color: 'white' }}
              />
            </Box>
            <Box sx={customChannelSelectorStyles.showAll}>
              {
                searchValue.length > 0 &&
                <Box onClick={showAll} sx={{ display: 'flex', alignItems: 'center' }}><ResetIcon color={"#19B2AB"} /><span>{t(localizationKeys.ResetSearch_lower)}</span></Box>
              }
            </Box>
          </Box>
          <Box sx={styles.tableContainer}>
            {filteredChannels?.length ? (
              <TableContainer sx={styles.table}>
                <TableVirtuoso
                  style={{ width: "100%", height: "100%" }}
                  totalCount={filteredChannels.length}
                  data={filteredChannels}
                  components={VirtuosoTableComponents}
                  fixedHeaderContent={TableHeader}
                  itemContent={(_index, row) => (
                    TableCustBody(row)
                  )}
                />
              </TableContainer>
            ) : (
              fetching ? (
                <Box sx={{ display: 'flex', height: '100%', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
                  <CircularProgress width='100%' height='100%' />
                </Box>)
                : (
                  < Box >
                    <Box sx={styles.tableMessage}>
                      {t(localizationKeys.ChannelGroupCountNote)}
                    </Box>
                    <Box sx={styles.noMatchFound}>
                      <span>{t(localizationKeys.CustomNoMatchFound)}</span>
                    </Box>
                  </Box>
                )
            )}
          </Box>
        </Grid>
        <Grid item size={{ xs: 6 }}>
          <Box sx={styles.linesContainer}>
            {channels.length > 0
              ? <Box sx={styles.lines}>{selectedChannels?.map(displaySelectedChannel)}</Box>
              : <Box sx={styles.lines}></Box>
            }
          </Box>
          <Box sx={styles.controller}>
            <Box sx={styles.controllerLeft}>
              <Button
                onClick={onSave}
                sx={selectedChannels?.length === 0 ? styles.disabledSubmitButton : styles.submitButton}
                disabled={selectedChannels?.length === 0}
              >
                {t(localizationKeys.ChannelGroupPromptSaveOkButton)}
              </Button>
              <Button sx={styles.cancelButton} onClick={onCancel}>
                {t(localizationKeys.CancelButtonText)}
              </Button>
            </Box>
            {isEditingGroup && (
              <Box sx={styles.controllerRight}>
                <IconButton onClick={onDelete} size="large">
                  <DeleteIcon />
                  <Box component="span" sx={styles.deleteButton}>{t(localizationKeys.CustomDelete)}</Box>
                </IconButton>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Grid>
  )
}

ChannelGroupRegisterPage.propTypes = {
  vesselId: PropTypes.string.isRequired,
  channelGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      values: PropTypes.array.isRequired
    })
  ).isRequired,
  setChannelGroups: PropTypes.func.isRequired,
  isCreatingNewGroup: PropTypes.bool.isRequired,
  setIsCreatingNewGroup: PropTypes.func.isRequired,
  selectedChannelGroup: PropTypes.object.isRequired,
  setSelectedChannelGroup: PropTypes.func.isRequired,
  previousSelectedChannelGroup: PropTypes.object,
  isEditingGroup: PropTypes.bool.isRequired,
  setIsEditingGroup: PropTypes.func.isRequired,
  setShowPrompt: PropTypes.func.isRequired,
  setPromptContent: PropTypes.func.isRequired,
  selectedChannels: PropTypes.array.isRequired,
  setSelectedChannels: PropTypes.func.isRequired,
  setIsUpdateFetch: PropTypes.func.isRequired,
}